import { createFrontendModule } from '@backstage/frontend-plugin-api';
import { createUnifiedTheme, UnifiedThemeProvider } from '@backstage/theme';
import { ThemeBlueprint } from '@backstage/plugin-app-react';
import DarkIcon from '@material-ui/icons/Brightness2';
import LightIcon from '@material-ui/icons/WbSunny';

const thinkHeader = (background: string) => ({
  colors: [background],
  shape: 'none',
  backgroundImage: background,
  fontColor: '#ffffff',
});

const thinkLightTheme = createUnifiedTheme({
  palette: {
    type: 'light',
    mode: 'light',
    primary: {
      main: '#7f4b9c',
      light: '#a77ac0',
      dark: '#5e3078',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#404041',
    },
    background: {
      default: '#f7f6f8',
      paper: '#ffffff',
    },
    status: {
      ok: '#1b7f4a',
      warning: '#a15c00',
      error: '#b3261e',
      running: '#1d4189',
      pending: '#8a6b00',
      aborted: '#6b6b6d',
    },
    border: '#dedbe2',
    textContrast: '#242427',
    textVerySubtle: '#b7b2ba',
    textSubtle: '#615d65',
    highlight: '#f3ebf8',
    errorBackground: '#fdecea',
    warningBackground: '#fff1dd',
    infoBackground: '#eaf0fb',
    errorText: '#8d1c16',
    infoText: '#18366f',
    warningText: '#624000',
    link: '#653a80',
    linkHover: '#4e2866',
    gold: '#c28a00',
    navigation: {
      background: '#404041',
      indicator: '#b98bd3',
      color: '#d8d5da',
      selectedColor: '#ffffff',
      navItem: {
        hoverBackground: '#515153',
      },
      submenu: {
        background: '#515153',
      },
    },
    tabbar: {
      indicator: '#7f4b9c',
    },
    pinSidebarButton: {
      icon: '#404041',
      background: '#d8d5da',
    },
    banner: {
      info: '#1d4189',
      error: '#b3261e',
      warning: '#a15c00',
      text: '#ffffff',
      link: '#ffffff',
      closeButtonColor: '#ffffff',
    },
    bursts: {
      fontColor: '#ffffff',
      slackChannelText: '#d8d5da',
      backgroundColor: { default: '#7f4b9c' },
      gradient: { linear: 'none' },
    },
  },
  defaultPageTheme: 'home',
  pageTheme: {
    home: thinkHeader('#404041'),
  },
});

const thinkDarkTheme = createUnifiedTheme({
  palette: {
    type: 'dark',
    mode: 'dark',
    primary: {
      main: '#d5b9e5',
      light: '#eadcf2',
      dark: '#b98bd3',
      contrastText: '#242427',
    },
    secondary: {
      main: '#e6e1e8',
    },
    background: {
      default: '#202024',
      paper: '#2a292e',
    },
    status: {
      ok: '#6cc58e',
      warning: '#f0b45d',
      error: '#f28b82',
      running: '#8eb6f2',
      pending: '#e3c55b',
      aborted: '#b3afb5',
    },
    border: '#45434a',
    textContrast: '#f4f1f5',
    textVerySubtle: '#87828c',
    textSubtle: '#c9c4cc',
    highlight: '#403848',
    errorBackground: '#4a2a2a',
    warningBackground: '#493b27',
    infoBackground: '#29344a',
    errorText: '#ffc0b9',
    infoText: '#c7d8ff',
    warningText: '#ffe0a3',
    link: '#dfc7ed',
    linkHover: '#f0e3f7',
    gold: '#e3c55b',
    navigation: {
      background: '#18181b',
      indicator: '#d5b9e5',
      color: '#c9c4cc',
      selectedColor: '#ffffff',
      navItem: {
        hoverBackground: '#302e34',
      },
      submenu: {
        background: '#302e34',
      },
    },
    tabbar: {
      indicator: '#d5b9e5',
    },
    pinSidebarButton: {
      icon: '#202024',
      background: '#c9c4cc',
    },
    banner: {
      info: '#547cc2',
      error: '#c95c56',
      warning: '#b67820',
      text: '#ffffff',
      link: '#ffffff',
      closeButtonColor: '#ffffff',
    },
    bursts: {
      fontColor: '#ffffff',
      slackChannelText: '#c9c4cc',
      backgroundColor: { default: '#7f4b9c' },
      gradient: { linear: 'none' },
    },
  },
  defaultPageTheme: 'home',
  pageTheme: {
    home: thinkHeader('#242427'),
  },
});

export const ThinkLightTheme = ThemeBlueprint.make({
  name: 'light',
  params: {
    theme: {
      id: 'light',
      title: 'THiNK light',
      variant: 'light',
      icon: <LightIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={thinkLightTheme} themeName="think-light">
          {children}
        </UnifiedThemeProvider>
      ),
    },
  },
});

export const ThinkDarkTheme = ThemeBlueprint.make({
  name: 'dark',
  params: {
    theme: {
      id: 'dark',
      title: 'THiNK dark',
      variant: 'dark',
      icon: <DarkIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={thinkDarkTheme} themeName="think-dark">
          {children}
        </UnifiedThemeProvider>
      ),
    },
  },
});

export const themeModule = createFrontendModule({
  pluginId: 'app',
  extensions: [ThinkLightTheme, ThinkDarkTheme],
});
